import { useEffect, useMemo, useRef, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../hooks/useAuth";
import { getEffectiveReminder } from "../data/usHolidayPresets.js";

/**
 * Subscribes to reminders for each giftee id and merges into one list
 * (each row includes `gifteeId`).
 */
export function useAllReminders(giftees) {
  const { currentUser } = useAuth();
  const idsKey = giftees.map((g) => g.id).join(",");
  const gifteeIds = useMemo(() => giftees.map((g) => g.id), [idsKey]);
  const [lists, setLists] = useState({});
  const [loading, setLoading] = useState(true);
  const gateRef = useRef(new Set());

  useEffect(() => {
    gateRef.current = new Set();
    if (!currentUser || gifteeIds.length === 0) {
      setLists({});
      setLoading(false);
      return;
    }

    setLoading(true);
    setLists({});

    const unsubs = gifteeIds.map((gid) => {
      const col = collection(
        db,
        "users",
        currentUser.uid,
        "giftees",
        gid,
        "reminders"
      );
      const q = query(col, orderBy("date", "asc"));
      return onSnapshot(
        q,
        (snap) => {
          const rows = snap.docs.map((d) =>
            getEffectiveReminder({
              id: d.id,
              gifteeId: gid,
              ...d.data(),
            }),
          );
          setLists((prev) => ({ ...prev, [gid]: rows }));
          gateRef.current.add(gid);
          if (gateRef.current.size >= gifteeIds.length) {
            setLoading(false);
          }
        },
        () => {
          gateRef.current.add(gid);
          if (gateRef.current.size >= gifteeIds.length) {
            setLoading(false);
          }
        }
      );
    });

    return () => {
      unsubs.forEach((u) => u());
    };
  }, [currentUser, idsKey, gifteeIds]);

  const allReminders = useMemo(
    () => gifteeIds.flatMap((id) => lists[id] || []),
    [lists, idsKey, gifteeIds]
  );

  return { allReminders, loading };
}
